import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Alert,
  Chip,
  Divider,
  CircularProgress,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  QrCodeScanner,
  Search,
  Person,
  Email,
  Phone,
  Event,
  AttachMoney,
  CalendarToday,
  Receipt,
  Refresh,
} from '@mui/icons-material';
import { Html5Qrcode } from 'html5-qrcode';
import { orderApi } from '../services/api';

const QRScanner = () => {
  const [scanning, setScanning] = useState(false);
  const [scannedResult, setScannedResult] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [manualInput, setManualInput] = useState('');
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);

  useEffect(() => {
    return () => {
      stopScanner();
    };
  }, []);

  const stopScanner = async () => {
    if (html5QrCodeRef.current) {
      try {
        const state = html5QrCodeRef.current.getState();
        if (state === 2) { // SCANNING
          await html5QrCodeRef.current.stop();
        }
      } catch (err) {
        console.warn('Error stopping scanner:', err);
      }
      html5QrCodeRef.current = null;
    }
    setScanning(false);
  };

  const startScanner = async () => {
    setError(null);
    setSuccessMessage(null);
    setScannedResult(null);
    setOrderDetails(null);

    try {
      if (!html5QrCodeRef.current) {
        html5QrCodeRef.current = new Html5Qrcode('qr-reader');
      }

      setScanning(true);

      await html5QrCodeRef.current.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (decodedText) => {
          handleScanResult(decodedText);
          stopScanner();
        },
        () => {} // ignore scan failures
      );
    } catch (err) {
      console.error('Error starting scanner:', err);
      setError('Could not access camera. Please check permissions or use manual input.');
      setScanning(false);
    }
  };

  const handleScanResult = async (result) => {
    setScannedResult(result);
    await fetchOrderDetails(result);
  };

  const handleManualSearch = async () => {
    if (!manualInput.trim()) return;
    setScannedResult(manualInput.trim());
    await fetchOrderDetails(manualInput.trim());
  };

  const fetchOrderDetails = async (orderNumber) => {
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    setOrderDetails(null);

    try {
      // Use orderApi from centralized service which handles headers automatically
      const data = await orderApi.getOrderFeed();

      // Handle different response structures
      let ordersList = [];
      if (Array.isArray(data)) {
        ordersList = data;
      } else if (Array.isArray(data.data)) {
        ordersList = data.data;
      } else if (Array.isArray(data.orders)) {
        ordersList = data.orders;
      } else if (data.data?.orders && Array.isArray(data.data.orders)) {
        ordersList = data.data.orders;
      } else if (data.data?.data && Array.isArray(data.data.data)) {
        ordersList = data.data.data;
      }

      console.log('Order feed response:', data);
      console.log('Orders list length:', ordersList.length);
      
      const searchLower = orderNumber.toLowerCase().trim();
      const found = ordersList.find(order => {
        const values = Object.values(order).map(v => String(v).toLowerCase());
        return values.some(v => v === searchLower || v.includes(searchLower) || searchLower.includes(v));
      });

      if (!found) {
        throw new Error(`Order "${orderNumber}" not found. ${ordersList.length} orders searched.`);
      }

      setOrderDetails(found);
    } catch (err) {
      console.error('Error fetching order:', err);
      // More robust error message extraction
      const apiErrorMsg = err.response?.data?.message || err.response?.data?.error || err.message;
      setError(apiErrorMsg || 'Failed to fetch order details');
      
      if (err.response?.status === 403) {
        setError('Access denied (403). Please make sure you are logged in as a moderator.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsUsed = async (orderNumber) => {
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const updatedOrder = await orderApi.markOrderAsUsed(orderNumber);
      setOrderDetails(updatedOrder);
      setSuccessMessage('Ticket marked as used successfully!');
    } catch (err) {
      console.error('Error marking order as used:', err);
      const apiErrorMsg = err.response?.data?.detail || err.response?.data?.message || err.response?.data?.error || err.message;
      setError(apiErrorMsg || 'Failed to mark ticket as used');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setScannedResult(null);
    setOrderDetails(null);
    setError(null);
    setSuccessMessage(null);
    setManualInput('');
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'success':
      case 'paid':
        return 'success';
      case 'pending':
      case 'processing':
        return 'warning';
      case 'failed':
      case 'cancelled':
      case 'canceled':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('ka-GE', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  const formatPrice = (amount) => {
    if (!amount && amount !== 0) return 'N/A';
    return `${amount} \u20BE`;
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          QR Scanner
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Scan order QR codes to verify tickets
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Scanner Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ textAlign: 'center' }}>
            <div id="qr-reader" style={{ width: '100%', maxWidth: 400, margin: '0 auto' }} />

            {!scanning && !scannedResult && (
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<QrCodeScanner />}
                  onClick={startScanner}
                  sx={{ mb: 3 }}
                >
                  Start Scanning
                </Button>

                <Divider sx={{ my: 2 }}>or</Divider>

                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mt: 2 }}>
                  <TextField
                    placeholder="Enter order number"
                    value={manualInput}
                    onChange={(e) => setManualInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleManualSearch()}
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Button variant="outlined" onClick={handleManualSearch}>
                    Search
                  </Button>
                </Box>
              </Box>
            )}

            {scanning && (
              <Button
                variant="outlined"
                color="error"
                onClick={stopScanner}
                sx={{ mt: 2 }}
              >
                Stop Scanning
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Loading */}
      {loading && (
        <Box display="flex" justifyContent="center" sx={{ my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Order Details */}
      {orderDetails && (
        <Card>
          <CardContent>
            {orderDetails.is_used && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                This ticket has already been marked as used / scanned.
              </Alert>
            )}
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Box display="flex" alignItems="center" gap={1}>
                <Receipt />
                <Typography variant="h6">Order Details</Typography>
              </Box>
              <Button startIcon={<Refresh />} onClick={handleReset}>
                Scan Another
              </Button>
            </Box>

            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Order Number</Typography>
                <Typography variant="body1" fontWeight={600}>
                  {orderDetails.order_number || orderDetails.number || orderDetails.id || scannedResult}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Status</Typography>
                <Chip
                  label={orderDetails.is_used ? 'Used / Scanned' : (orderDetails.status || orderDetails.payment_status || 'Unknown')}
                  color={orderDetails.is_used ? 'default' : getStatusColor(orderDetails.status || orderDetails.payment_status)}
                  size="small"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Created At</Typography>
                <Typography variant="body1">
                  {formatDate(orderDetails.created_at || orderDetails.date)}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Payment Method</Typography>
                <Typography variant="body1">
                  {orderDetails.payment_method || 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Customer Information</Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Person fontSize="small" />
                  <Typography variant="body2" color="text.secondary">Name</Typography>
                </Box>
                <Typography variant="body1">
                  {orderDetails.customer_name || orderDetails.name || 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Email fontSize="small" />
                  <Typography variant="body2" color="text.secondary">Email</Typography>
                </Box>
                <Typography variant="body1">
                  {orderDetails.customer_email || orderDetails.email || 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Phone fontSize="small" />
                  <Typography variant="body2" color="text.secondary">Phone</Typography>
                </Box>
                <Typography variant="body1">
                  {orderDetails.customer_phone || orderDetails.phone || 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Booking Details</Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Event fontSize="small" />
                  <Typography variant="body2" color="text.secondary">Activity/Event</Typography>
                </Box>
                <Typography variant="body1">
                  {orderDetails.activity_name || orderDetails.event_name || orderDetails.service_name || 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <CalendarToday fontSize="small" />
                  <Typography variant="body2" color="text.secondary">Event Date</Typography>
                </Box>
                <Typography variant="body1">
                  {formatDate(orderDetails.event_date || orderDetails.date)}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Number of People</Typography>
                <Typography variant="body1">
                  {orderDetails.people_count || orderDetails.quantity || orderDetails.participants || 1}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <AttachMoney fontSize="small" />
                  <Typography variant="body2" color="text.secondary">Total Amount</Typography>
                </Box>
                <Typography variant="h6" color="primary" fontWeight={600}>
                  {formatPrice(orderDetails.total_amount || orderDetails.amount || orderDetails.price)}
                </Typography>
              </Grid>

              {orderDetails.notes && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Notes</Typography>
                  <Typography variant="body1">{orderDetails.notes}</Typography>
                </Grid>
              )}
            </Grid>

            {/* Mark as Used Button */}
            {!orderDetails.is_used && (
              <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  color="warning"
                  size="large"
                  onClick={() => handleMarkAsUsed(orderDetails.order_number || orderDetails.number || orderDetails.id || scannedResult)}
                  disabled={loading}
                >
                  Mark as Used
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* No result found after scan */}
      {scannedResult && !orderDetails && !loading && !error && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Scanned: {scannedResult} - No order details found.
        </Alert>
      )}
    </Container>
  );
};

export default QRScanner;
